import { BillOrInvoiceRailzPaymentDetails, formattedCurrency, sanitizeToNumeric, withStyles } from 'vopay';
import { useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAccountingSoftware } from '../AccountingSoftwareContext';

import styles from './NestedTransactionsTable.module.scss';

import { TransactionStatus } from '#shared/components/UI';

const cn = withStyles(styles);

// One display row per money movement: a payment's transaction, and a separate
// row (sharing its No.) when part of it was settled with credit notes —
// mirroring what the old partially-paid dialog broke out.
type TransactionRow = {
  numPayment: number;
  date: string;
  reference: React.ReactNode;
  amount: string;
  status: BillOrInvoiceRailzPaymentDetails['Status'] | 'none';
};

type NestedTransactionsTableProps = {
  DocumentID: string;
  Payments: BillOrInvoiceRailzPaymentDetails[];
  Currency: string;
  ServiceName: string;
  // Amount settled on a closed document. When it carries no payment records
  // at all it was closed inside the accounting software, so the table shows
  // one "Paid using {service}" row for this amount instead of sitting empty.
  PaidAmount?: string;
};

// The design's sub table is a one-off (light bordered card, semibold header
// with vertical dividers) that doesn't match the app-wide Table styling, so
// its markup is hand-rolled here rather than fighting the shared component.
export default function NestedTransactionsTable({
  DocumentID,
  Payments,
  Currency,
  ServiceName,
  PaidAmount,
}: NestedTransactionsTableProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.NestedTransactions' });

  const {
    hostActions: { onNavigate },
  } = useAccountingSoftware();

  const statusLabels: { [status in Exclude<TransactionRow['status'], undefined>]: string } = {
    processing: t('statusInProgress', { defaultValue: 'In Progress' }),
    successful: t('statusSuccessful', { defaultValue: 'Successful' }),
    failed: t('statusFailed', { defaultValue: 'Failed' }),
    cancelled: t('statusCancelled', { defaultValue: 'Cancelled' }),
    none: '–',
  };

  const rows = useMemo(() => {
    const asAmount = (value: string) => Number(sanitizeToNumeric(value)) || 0;
    const asDate = (value: string) => (value ? format(new Date(value.replace('Z', '')), 'yyyy-MM-dd') : '');
    const transactionRows: TransactionRow[] = [];

    Payments.forEach((payment, index) => {
      if (asAmount(payment.TransactionAmount) > 0) {
        transactionRows.push({
          numPayment: index + 1,
          date: asDate(payment.TransactionDate),
          // PaymentRail is the origin: set = moved by VoPay (linkable once its
          // webhook stamps a TransactionID), null = recorded in the accounting
          // software with no VoPay transaction behind it.
          // Link-out via the host's onNavigate; plain (unstyled) text when the
          // host can't navigate.
          reference: payment.TransactionID ? (
            onNavigate ? (
              <button
                type="button"
                className={cn('transactionLink')}
                onClick={() =>
                  onNavigate(`/transactions-history/details/Transaction/${payment.TransactionID}/${Currency}`)
                }
              >
                {payment.TransactionID}
              </button>
            ) : (
              <span>{payment.TransactionID}</span>
            )
          ) : payment.PaymentRail ? (
            t('paymentProcessing', { defaultValue: 'Payment processing' })
          ) : (
            t('paidUsing', { defaultValue: 'Paid using {{serviceName}}', serviceName: ServiceName })
          ),
          amount: `${Currency} ${formattedCurrency(asAmount(payment.TransactionAmount).toFixed(2))}`,
          // Payments recorded in the accounting software have no VoPay status.
          status: payment.PaymentRail ? payment.Status || 'none' : 'none',
        });
      }

      if (asAmount(payment.CreditAmount) > 0) {
        transactionRows.push({
          numPayment: index + 1,
          date: asDate(payment.TransactionDate),
          reference: t('paidUsingCredits', { defaultValue: 'Paid using Credits' }),
          amount: `${Currency} ${formattedCurrency(asAmount(payment.CreditAmount).toFixed(2))}`,
          status: 'none',
        });
      }
    });

    if (transactionRows.length === 0 && PaidAmount !== undefined) {
      transactionRows.push({
        numPayment: 1,
        // The accounting software reports no payment records, so there is no
        // date to show for when it was paid.
        date: '',
        reference: t('paidUsing', { defaultValue: 'Paid using {{serviceName}}', serviceName: ServiceName }),
        amount: `${Currency} ${formattedCurrency(asAmount(PaidAmount).toFixed(2))}`,
        status: 'none',
      });
    }

    return transactionRows;
  }, [Payments, Currency, ServiceName, PaidAmount, onNavigate, t]);

  return (
    <table className={cn('subTable')} data-testid={`table-nested-transactions-${DocumentID}`}>
      <thead>
        <tr>
          <th className={cn('colNumber')}>{t('colNumber', { defaultValue: 'No.' })}</th>
          <th className={cn('colDate')}>{t('colDate', { defaultValue: 'Date' })}</th>
          <th className={cn('colReference')}>{t('colTransactionID', { defaultValue: 'Trans. ID' })}</th>
          <th>{t('colAmount', { defaultValue: 'Amount' })}</th>
          <th className={cn('lastHeader')}>{t('colStatus', { defaultValue: 'Transaction Status' })}</th>
        </tr>
      </thead>

      <tbody>
        {rows.map((row, index) => (
          <tr key={`${DocumentID}-${index}`}>
            <td>{row.numPayment}</td>
            <td>{row.date}</td>
            <td>{row.reference}</td>
            <td>{row.amount}</td>

            <td>
              {row.status && row.status !== 'none' ? (
                // The chip is flex and would stretch across the cell — keep it
                // hugging its content.
                <span className={cn('statusChip')}>
                  <TransactionStatus status={row.status} label={statusLabels[row.status]} />
                </span>
              ) : (
                <span className={cn('noStatus')}>{statusLabels.none}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
